import Controller from '@ember/controller';
import { action } from '@ember/object';
import { equal, filterBy } from '@ember/object/computed';
import { isPresent } from '@ember/utils';
import { inject as service } from '@ember/service';
import { reads } from '@ember/object/computed';
import { task } from 'ember-concurrency';
import { UserRoleType, USER_ROLE_ADMIN, USER_ROLES, USER_ROLES_LABELS } from 'cio/utils/user-roles';
import FormValidations from 'cio/utils/form-validations';
import UserModel from 'cio/models/user';
import AccountService from 'cio/services/account';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import MasqService from 'cio/services/masq';
import SessionService from 'cio/services/session';
import AnalyticsService from 'cio/services/analytics';
import SsoService from 'cio/services/sso';
import OAuthService from 'cio/services/oauth';
import ApiService from 'cio/services/api';
import { Provider } from 'cio/utils/oauth';
import { setAccessCookie } from 'cio/utils/auth-cookies';

enum ModalStates {
  Closed = 'closed',
  SetExternalIdentity = 'setExternalIdentity',
  DestroyExternalIdentity = 'destroyExternalIdentity',
}

const Validations = {
  _newUserRole(this: SettingsPersonalIndexController, _value: UserRoleType, model: UserModel) {
    let isRemovingOwnAdmin =
      isPresent(this._newUserRole) && this._newUserRole !== USER_ROLE_ADMIN && model.role === USER_ROLE_ADMIN;
    if (isRemovingOwnAdmin && this.hasOnlyOneAccountAdmin) {
      return 'At least one user must remain an admin';
    }

    return true;
  },
};

export default class SettingsPersonalIndexController extends Controller {
  queryParams = ['isEditingPassword', 'isEditingEmail'];

  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service oauth!: OAuthService;
  @service masq!: MasqService;
  @service session!: SessionService;
  @service sso!: SsoService;

  declare model: { user: UserModel; users: UserModel[] };

  // for template usage:
  ModalStates = ModalStates;
  _userRolesLabels = USER_ROLES_LABELS;
  _userRolesSelectOptions = USER_ROLES.map((value) => ({ label: USER_ROLES_LABELS[value], value }));

  _validations: FormValidations;
  isEditingPassword = false;
  isEditingEmail = false;
  modal: ModalStates = ModalStates.Closed;

  _currentPassword2FA = '';

  @reads('model.user.firstName') _newUserFirstName!: string;
  @reads('model.user.lastName') _newUserLastName!: string;
  @reads('model.user.role') _newUserRole!: string;
  @reads('model.user.email') _newUserEmail!: string;
  @filterBy('model.users', 'role', 'admin') adminUsers!: UserModel[];
  @equal('adminUsers.length', 1) hasOnlyOneAccountAdmin!: boolean;

  constructor() {
    super(...arguments);

    this._validations = FormValidations.create({ validations: Validations, context: this, modelKey: 'model.user' });
  }

  get hasEditSettingsPermission() {
    return this.auth.hasPermission('account/personal_settings/edit');
  }

  updateUser = task({ drop: true }, async (submitEvent: Event) => {
    submitEvent.preventDefault();

    let { user } = this.model;

    await this._validations.validateModel();

    let {
      _newUserFirstName: firstName,
      _newUserLastName: lastName,
      _newUserRole: role,
    } = this.getProperties('_newUserFirstName', '_newUserLastName', '_newUserRole');

    user.setProperties({ firstName, lastName, role });

    try {
      await user.save();

      this.alerts.success('user.update.success', 'User data updated');
      this.analytics.track('user', 'settings_edited');

      if (user.role !== USER_ROLE_ADMIN) {
        this.send('refreshModel');
      }
    } catch (e) {
      this.apiErrors.report(e);
      user.rollbackAttributes();
    }
  });

  setExternalIdentityTask = task(async () => {
    try {
      let { authorizationCode, redirectUri } = await this.oauth.open(Provider.GoogleOIDC);

      if (authorizationCode == null || redirectUri == null) {
        throw new Error('missing authorizationCode or redirectUri');
      }

      await this.model.user.setExternalIdentity({
        code: authorizationCode,
        redirectUri,
        currentPassword: this._currentPassword2FA,
      });
      await this.model.user.reload();

      this.alerts.success('user.setExternalIdentity', 'You can now use this account to sign into Customer.io');
    } catch {
      this.alerts.error('user.destroyExternalIdentity', 'Something went wrong! Please try again later.');
    }

    this.set('modal', ModalStates.Closed);
    this.set('_currentPassword2FA', '');
  });

  destroyExternalIdentityTask = task(async () => {
    try {
      await this.model.user.destroyExternalIdentity({});
      await this.model.user.reload();

      this.alerts.success('user.destroyExternalIdentity', 'Linked account removed');
    } catch {
      this.alerts.error('user.destroyExternalIdentity', 'Something went wrong! Please try again later.');
    }

    this.set('modal', ModalStates.Closed);
  });

  resetDefaults() {
    this.setProperties({ isEditingPassword: false, isEditingEmail: false, modal: ModalStates.Closed });
  }

  @action
  cancel() {
    this.model.user.rollbackAttributes();
  }

  @action
  async setEmail(user: UserModel, email: string, currentPassword: string) {
    this.apiErrors.clear();
    try {
      await user.setEmail(email, currentPassword);
      user.setProperties({ email });

      this.alerts.success('user.update.success', 'User email updated');
      this.analytics.track('user', 'settings_edited');

      this.setProperties({ isEditingEmail: false });

      if (user.role !== USER_ROLE_ADMIN) {
        this.send('refreshModel');
      }
    } catch (e) {
      this.apiErrors.report(e);
      user.rollbackAttributes();
    }
  }

  @action
  setUserRole(value: UserRoleType) {
    this.set('_newUserRole', value);
    this._validations.validateAttribute('_newUserRole');
  }

  @action
  toggleEditingPassword() {
    this.toggleProperty('isEditingPassword');
  }

  @action
  toggleEditingEmail() {
    this.toggleProperty('isEditingEmail');
  }

  @action
  async setPassword(user: UserModel, newPassword: string, currentPassword: string) {
    try {
      let { accessToken } = await user.setPassword(newPassword, currentPassword);

      this.set('isEditingPassword', false);
      this.alerts.success('user.setPassword.success', 'Password updated');

      setAccessCookie(accessToken);
      // generate new session: so ember-simple-auth holds updated _access cookie in its session data
      this.session.authenticate('authenticator:cookie-access-token');
    } catch (e) {
      this.apiErrors.report(e);
    }
  }

  @action setExternalIdentityStart() {
    this.analytics.track('user', 'change_google_account_started');
    this.set('modal', ModalStates.SetExternalIdentity);
    return;
  }

  @action setCurrentPassword(currentPassword: string) {
    this.set('_currentPassword2FA', currentPassword);
  }

  @action destroyExternalIdentityStart() {
    this.analytics.track('user', 'unlink_google_account_started');
    this.set('modal', ModalStates.DestroyExternalIdentity);
  }
}
